import axios from "axios";
import { config } from '../../config/Config';

const invitationUrl = `${config.serverBaseUrl}/v1/web/invitation`;

const authHeaders = (accessToken) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`
});

export const getAllInvitations = async ({ accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${invitationUrl}/all`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching all invitations:', error.response?.data || error.message);
        throw error;
    }
};

export const getInvitationById = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${invitationUrl}/${invitationId}`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching invitation by ID:', error.response?.data || error.message);
        throw error;
    }
};

export const sendInvitation = async ({ accessToken, invitationData }) => {
    try {
        const response = await axios({
            method: 'post',
            url: invitationUrl,
            headers: authHeaders(accessToken),
            data: invitationData
        });
        return response.data;
    } catch (error) {
        console.error('Error sending invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const requestAccess = async ({ accessToken, requestData }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/request-access`,
            headers: authHeaders(accessToken),
            data: requestData
        });
        return response.data;
    } catch (error) {
        console.error('Error requesting access:', error.response?.data || error.message);
        throw error;
    }
};

export const acceptInvitation = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/${invitationId}/accept`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error accepting invitation:', error.response?.data || error.message);
        throw error;
    }
};

// Creates the user record (persisting their display name) AND accepts the
// invitation in one call — used for brand-new invitees in the guided accept flow.
export const createUserAndAcceptInvitation = async ({ accessToken, invitationId, name, title, selectedPreferenceIds = [] }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/create-user-and-accept`,
            headers: authHeaders(accessToken),
            data: { invitationId, name, title, selectedPreferenceIds }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating user and accepting invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const resendInvitation = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/${invitationId}/resend`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error resending invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const declineInvitation = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/${invitationId}/decline`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error declining invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const revokeInvitation = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/${invitationId}/revoke`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error revoking invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const approveInvitation = async ({ accessToken, invitationId }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${invitationUrl}/${invitationId}/approve`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error approving invitation:', error.response?.data || error.message);
        throw error;
    }
};

export const getPendingApprovals = async ({ accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${invitationUrl}/pending-approvals`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching pending approvals:', error.response?.data || error.message);
        throw error;
    }
};

export const getInitiatedInvitations = async ({ accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${invitationUrl}/initiated`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching initiated invitations:', error.response?.data || error.message);
        throw error;
    }
};

// The caller's own access requests (with status). Unlike /initiated this is NOT permission-gated,
// so a user with no org/role yet (access-pending / declined) can read it. Shape: { invitations }.
export const getMyAccessRequests = async ({ accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${invitationUrl}/my-access-requests`,
            headers: authHeaders(accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching my access requests:', error.response?.data || error.message);
        throw error;
    }
};